import React from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import DataTable from '../../components/ui/data-table';
import EmptyState from '../../components/ui/empty-state.jsx';
import LoadingSpinner from '../../components/common/LoadingSpinner.jsx';
import { useExams } from '../../hooks/useExams';
import { Calendar, Plus, Clock, Users, Award, BarChart3, Edit, Eye, FileText } from 'lucide-react';

const TeacherExams = () => {
  const { exams, loading, error, fetchExams } = useExams({ scope: 'teacher' });

  const columns = [
    { id: 'title', header: 'Exam Title', accessorKey: 'title', sortable: true },
    { id: 'subject', header: 'Subject', accessorKey: 'subjectName', sortable: true },
    { id: 'class', header: 'Class', accessorKey: 'className' },
    { id: 'examDate', header: 'Date', accessorKey: 'examDate', sortable: true },
    { id: 'startTime', header: 'Start Time', accessorKey: 'startTime' },
    { id: 'duration', header: 'Duration', accessorKey: 'duration' },
    { id: 'status', header: 'Status', accessorKey: 'status', cell: ({ row }) => {
      const status = row.original.status;
      const statusColors = {
        draft: 'bg-gray-100 text-gray-800',
        scheduled: 'bg-blue-100 text-blue-800',
        active: 'bg-green-100 text-green-800',
        completed: 'bg-purple-100 text-purple-800',
        graded: 'bg-indigo-100 text-indigo-800'
      };
      return (
        <Badge className={statusColors[status] || 'bg-gray-100 text-gray-800'}>
          {status}
        </Badge>
      );
    }},
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => window.location.assign(`/dashboard/exams/${row.original.id}`)}>
            <Eye className="w-4 h-4" />
          </Button>
          <Button size="sm" variant="outline" onClick={() => window.location.assign(`/dashboard/exams/${row.original.id}/edit`)}>
            <Edit className="w-4 h-4" />
          </Button>
          <Button size="sm" variant="outline" onClick={() => window.location.assign(`/dashboard/exams/${row.original.id}/grade`)}>
            <Award className="w-4 h-4" />
          </Button>
          <Button size="sm" variant="outline" onClick={() => window.location.assign(`/dashboard/exams/${row.original.id}/analytics`)}>
            <BarChart3 className="w-4 h-4" />
          </Button>
        </div>
      )
    }
  ];

  const upcomingExams = exams?.filter(exam => 
    exam.status === 'scheduled' || exam.status === 'ongoing'
  ) || [];

  const completedExams = exams?.filter(exam => 
    exam.status === 'completed' || exam.status === 'graded'
  ) || [];

  const draftExams = exams?.filter(exam => exam.status === 'draft') || [];

  const getExamStats = () => {
    return {
      total: exams?.length || 0,
      upcoming: upcomingExams.length,
      completed: completedExams.length,
      drafts: draftExams.length,
      graded: exams?.filter(exam => exam.status === 'graded').length || 0
    };
  };

  const stats = getExamStats();

  if (loading) return <LoadingSpinner />;
  if (error) return (
    <div className="p-6">
      <Card>
        <CardContent className="p-6">
          <p className="text-red-600 text-sm">{String(error)}</p>
          <Button className="mt-3" onClick={() => fetchExams()}>Retry</Button>
        </CardContent>
      </Card>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold">Exam Management</h2>
          <p className="text-gray-600">Create, schedule, and manage your exams</p>
        </div>
        <Button onClick={() => window.location.assign('/dashboard/exams/create')} className="flex items-center gap-2">
          <Plus className="w-4 h-4" />
          Create New Exam
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Calendar className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Total Exams</p>
                <p className="text-2xl font-semibold">{stats.total}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Clock className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Upcoming</p>
                <p className="text-2xl font-semibold">{stats.upcoming}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Users className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Completed</p>
                <p className="text-2xl font-semibold">{stats.completed}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-indigo-100 rounded-lg">
                <Award className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Graded</p>
                <p className="text-2xl font-semibold">{stats.graded}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gray-100 rounded-lg">
                <Edit className="w-5 h-5 text-gray-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Drafts</p>
                <p className="text-2xl font-semibold">{stats.drafts}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card>
        <CardContent className="p-4">
          <h3 className="font-semibold mb-3">Quick Actions</h3>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => window.location.assign('/dashboard/exams/create')}>
              <Plus className="w-4 h-4 mr-2" />
              Create Exam
            </Button>
            <Button variant="outline" onClick={() => window.location.assign('/dashboard/exams/create?type=mcq')}>
              <Calendar className="w-4 h-4 mr-2" />
              MCQ Exam
            </Button>
            <Button variant="outline" onClick={() => window.location.assign('/dashboard/exams/create?type=essay')}>
              <FileText className="w-4 h-4 mr-2" />
              Essay Exam
            </Button>
            <Button variant="outline" onClick={() => window.location.assign('/dashboard/exams/create?type=mixed')}>
              <BarChart3 className="w-4 h-4 mr-2" />
              Mixed Exam
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Exams Tabs */}
      <Tabs defaultValue="all" className="space-y-4">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="all">All Exams ({stats.total})</TabsTrigger>
          <TabsTrigger value="upcoming">Upcoming ({stats.upcoming})</TabsTrigger>
          <TabsTrigger value="completed">Completed ({stats.completed})</TabsTrigger>
          <TabsTrigger value="drafts">Drafts ({stats.drafts})</TabsTrigger>
        </TabsList>

        <TabsContent value="all">
          {(!exams || exams.length === 0) ? (
            <EmptyState 
              icon={Calendar}
              title="No exams yet"
              description="Create and schedule your first exam to get started."
              action
              actionLabel="Create Exam"
              onAction={() => window.location.assign('/dashboard/exams/create')}
            />
          ) : (
            <Card>
              <CardContent className="p-0">
                <DataTable data={exams} columns={columns} searchable />
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="upcoming">
          {upcomingExams.length === 0 ? (
            <EmptyState 
              icon={Clock}
              title="No upcoming exams"
              description="Schedule exams to see them here."
              action
              actionLabel="Create Exam"
              onAction={() => window.location.assign('/dashboard/exams/create')}
            />
          ) : (
            <Card>
              <CardContent className="p-0">
                <DataTable data={upcomingExams} columns={columns} searchable />
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="completed">
          {completedExams.length === 0 ? (
            <EmptyState 
              icon={Users}
              title="No completed exams"
              description="Completed exams will appear here."
            />
          ) : (
            <Card>
              <CardContent className="p-0">
                <DataTable data={completedExams} columns={columns} searchable />
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="drafts">
          {draftExams.length === 0 ? (
            <EmptyState 
              icon={Edit}
              title="No draft exams"
              description="Save exams as drafts to continue working on them later."
              action
              actionLabel="Create Exam"
              onAction={() => window.location.assign('/dashboard/exams/create')}
            />
          ) : (
            <Card>
              <CardContent className="p-0">
                <DataTable data={draftExams} columns={columns} searchable />
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default TeacherExams;


